import {decode} from 'geobuf';
import PBF from 'pbf';
import type {Gallery} from './api-common.ts';
import {BASE_API_URL} from '../envs.ts';
import type {
  LandCode,
  RouteCategoryCode,
  SeasonCode,
} from '../layers/layerTypes.ts';
import type {LanguageCode} from '../i18n.ts';
import type {GroupFilter} from './api-groups.ts';
import type {MultiLineString} from 'geojson';
import {HttpError} from './api-tours.ts';

export type RouteOrSegmentFilters = {
  season: SeasonCode;
  land: LandCode;
  categories: Record<RouteCategoryCode, boolean>;
  grades: Record<RouteGrade, boolean>;
  gradesSkiing: Record<RouteGradeSkiing, boolean>;
  fitness: Record<RouteFitnessLevel, boolean>;
  canton: number;
  specialInterests: {
    day: boolean;
    bookable: boolean;
    groups: Record<LandCode, GroupFilter[]>;
  };
  structures: boolean;
};

/**
 * The route technical difficulty.
 * easy: hiking trail
 * medium: mountain hiking trail
 * difficult: ...
 */
export type RouteGrade = 'easy' | 'medium' | 'difficult';

/**
 * The route physical difficulty.
 */
export type RouteFitnessLevel = 'easy' | 'medium' | 'difficult';

/**
 * Cross-country skiing grade.
 */
export type RouteGradeSkiing = 'classic' | 'skating' | 'both';

/**
 * A Land card usable for basic filtering.
 * See https://www.schweizmobil.ch/en/hiking-in-switzerland/routes/national-routes.html
 */
export type RouteOrSegmentBasic = {
  // for navigating
  routeNumber: number;
  segmentNumber: number;
  // for filtering
  touristicRegions: number[]; // the touristic regions associated to the route
  cantons: number[]; // the cantons associated to the route
  category: RouteCategoryCode;
  stages: number; // number of segments
  grade: RouteGrade | null;
  gradeText?: string;
  gradeSkiing?: RouteGradeSkiing | null;
  fitness: RouteFitnessLevel | null;
  fitnessText?: string;
  length: number; // in km
  lengthAsphalt?: number;
  lengthSingletrail?: number;
  lengthUnsurfaced?: number;
  ascent: number; // in meters
  descent: number; // in meters
  elemin: number;
  elemax: number;
  // for displaying only
  title: string; // like Via Alpina
  start: string; // the title_start
  via?: string; // this is an opaque string
  end: string; // the title_end
  imgSrc: string;
  imgUrls: Record<string, string>;
  imgBigSrc: string;
  imgAbstract: string;
  logoSrc: string;
  logoSvgSrc?: string;
  land: LandCode;
  bookableIds: number[];
  groupIds: number[];
  sightseeingIds: number[];
  signalWay: 'bothways' | 'oneway';
  placeIds: number[];
  accommodationIds: number[];
  cycleserviceIds: number[];
  velorentalIds: number[];
  travelreportIds: number[];
  groupNumbers: number[];
  arrivalIds: number[];
  directionIds: number[];
  detourIds: number[];
  goodtoknows: string[]; // id_type array, type can be Arrival | Direction | Detour
  profileGeom: [][][];
};

export type RouteSegmentGallery = Gallery & {
  id: number;
  coordinates: number[];
};

export type DetailedRouteOrSegment = RouteOrSegmentBasic & {
  abstract: string;
  description: string;
  gallery: RouteSegmentGallery[];
  hikingTime: string;
  segments: SimpleSegment[];
  heightProfileUrl?: string;
};

// route response contains all properties, segment response only segmentNumber
export type SimpleSegment = {
  segmentNumber: number;
  start?: string;
  via?: string;
  end?: string;
  length?: number;
  ascent?: number;
  descent?: number;
};

export type RouteSegmentNumber = {
  routeNumber: number;
  segmentNumber: number;
};

interface RoutesGetOptions {
  limit?: boolean;
}

export async function fetchSegments(
  land: LandCode,
  category: RouteCategoryCode,
  options: RoutesGetOptions = {},
): Promise<RouteOrSegmentBasic[]> {
  const service = options.limit ? '/api/4/segments_limited' : '/api/4/segments';
  const response: Response = await fetch(
    `${BASE_API_URL}${service}/${land}/${category}`,
  );
  return response.json() as Promise<RouteOrSegmentBasic[]>;
}

export async function fetchRoutes(
  land: LandCode,
  category: RouteCategoryCode,
  lang: LanguageCode,
  options: RoutesGetOptions = {},
): Promise<RouteOrSegmentBasic[]> {
  const service = options.limit ? '/api/4/routes_limited' : '/api/4/routes';
  const response: Response = await fetch(
    `${BASE_API_URL}${service}/${land}/${category}?lang=${lang}`,
  );
  return response.json() as Promise<RouteOrSegmentBasic[]>;
}

export async function fetchRouteOrSegment(
  land: LandCode,
  routeNumber: number,
  segmentNumber: number,
  lang: LanguageCode,
): Promise<DetailedRouteOrSegment> {
  const response: Response = await fetch(
    `${BASE_API_URL}/api/4/route_or_segment/${land}/${routeNumber}/${segmentNumber}?lang=${lang}`,
  );
  if (response.ok) {
    return response.json() as Promise<DetailedRouteOrSegment>;
  } else {
    throw new HttpError(response.status, response.statusText);
  }
}

export async function fetchRouteOrSegmentFeature(
  land: LandCode,
  routeNumber: number,
  segmentNumber: number,
): Promise<GeoJSON.Feature> {
  const response = await fetch(
    `${BASE_API_URL}/api/4/route_or_segment/${land}/${routeNumber}/${segmentNumber}.geobuf`,
  );
  const data = await response.arrayBuffer();
  // geobuf from PostGIS is always a FeatureCollection with one feature
  const featureCollection = decode(new PBF(data)) as GeoJSON.FeatureCollection;
  if (featureCollection.features.length !== 1) {
    throw new Error('Invalid features length in geobuf');
  } else {
    return featureCollection.features[0];
  }
}

export async function fetchRouteOrSegmentGeometryJSON(
  routeNumber: number,
  segmentNumber: number,
  land: string,
): Promise<MultiLineString> {
  const response = await fetch(
    `${BASE_API_URL}/api/4/route_or_segment/${land}/${routeNumber}/${segmentNumber}.json?srid=2056`,
  );
  if (response.ok) {
    return response.json() as Promise<MultiLineString>;
  }
  return undefined;
}

export async function fetchRouteSegmentNumber(
  segmentWebdbId: number,
): Promise<RouteSegmentNumber | undefined> {
  const response = await fetch(
    `${BASE_API_URL}/api/4/route_segment_number/${segmentWebdbId}`,
  );
  if (response.ok) {
    return response.json() as Promise<RouteSegmentNumber>;
  }
  return undefined;
}
